import React, { useState } from 'react';
import styled from 'styled-components';
import { generateInviteLink, detectDevice, getStoreUrls } from '../utils/deepLink';

const TestContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const TestSection = styled.div`
  background: white;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  color: #D2691E;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
`;

const Button = styled.button`
  background: #D2691E;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 10px 20px;
  font-weight: 600;
  cursor: pointer;
  margin-right: 10px;
  margin-bottom: 10px;
  
  &:hover {
    background: #B8591A;
  }
`;

const ResultBox = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  padding: 15px;
  margin-top: 15px;
  word-break: break-all;
  font-family: monospace;
  font-size: 14px;
`;

const DeviceInfo = styled.div`
  background: #e3f2fd;
  border-left: 4px solid #2196f3;
  padding: 15px;
  margin-bottom: 20px;
`;

const InviteTest = () => {
  const [formData, setFormData] = useState({
    userId: 'user123',
    userName: '테스트유저'
  });
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  
  const device = detectDevice();
  const storeUrls = getStoreUrls();
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleGenerateLink = () => {
    const link = generateInviteLink(formData);
    setGeneratedLink(link);
    setCopied(false);
  };
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('복사 실패:', error);
    }
  };
  
  const handleTestLink = () => {
    if (generatedLink) {
      window.open(generatedLink, '_blank');
    }
  };
  
  return (
    <TestContainer>
      <Title>딥링크 테스트 도구</Title>
      
      <DeviceInfo>
        <h3>현재 기기 정보</h3>
        <p>iOS: {device.isIOS ? '예' : '아니오'}</p>
        <p>Android: {device.isAndroid ? '예' : '아니오'}</p>
        <p>모바일: {device.isMobile ? '예' : '아니오'}</p>
        <p>데스크톱: {device.isDesktop ? '예' : '아니오'}</p>
        <p>User Agent: {navigator.userAgent}</p>
      </DeviceInfo>
      
      <TestSection>
        <Title>초대 링크 생성</Title>
        
        <FormGroup>
          <Label>사용자 ID</Label>
          <Input
            type="text"
            name="userId"
            value={formData.userId}
            onChange={handleInputChange}
            placeholder="사용자 ID를 입력하세요"
          />
        </FormGroup>
        
        <FormGroup>
          <Label>사용자 이름</Label>
          <Input
            type="text"
            name="userName"
            value={formData.userName}
            onChange={handleInputChange}
            placeholder="사용자 이름을 입력하세요"
          />
        </FormGroup>
        
        <Button onClick={handleGenerateLink}>링크 생성</Button>
        
        {generatedLink && (
          <>
            <ResultBox>{generatedLink}</ResultBox>
            <div style={{ marginTop: '10px' }}>
              <Button onClick={handleCopyLink}>
                {copied ? '복사됨!' : '링크 복사'}
              </Button>
              <Button onClick={handleTestLink}>링크 테스트</Button>
            </div>
          </>
        )}
      </TestSection>
      
      <TestSection>
        <Title>스토어 링크</Title>
        <p>App Store: <a href={storeUrls.ios} target="_blank" rel="noopener noreferrer">{storeUrls.ios}</a></p>
        <p>Play Store: <a href={storeUrls.android} target="_blank" rel="noopener noreferrer">{storeUrls.android}</a></p>
      </TestSection>
      
      <TestSection>
        <Title>사용 방법</Title>
        <ol>
          <li>위 폼에서 사용자 ID와 사용자 이름을 입력합니다.</li>
          <li>"링크 생성" 버튼을 클릭하여 초대 링크를 생성합니다.</li>
          <li>"링크 테스트" 버튼을 클릭하여 생성된 링크를 새 탭에서 테스트합니다.</li>
          <li>모바일 기기에서 테스트하면 앱 설치 여부에 따라 앱이 열리거나 스토어로 이동합니다.</li>
        </ol>
        
        <Title>딥링크 스킴</Title>
        <p><strong>앱에서 처리해야 할 URL 스킴:</strong></p>
        <code>lengsup://invite?userId={'{사용자ID}'}&userName={'{사용자이름}'}</code>
        
        <Title>앱에서 링크 생성 방법</Title>
        <p>앱에서는 다음과 같이 웹 링크를 생성하면 됩니다:</p>
        <code>https://lengsup-admin.web.app/invite?userId={'{사용자ID}'}&userName={'{사용자이름}'}</code>
      </TestSection>
    </TestContainer>
  );
};

export default InviteTest;